/**
 * Tier II Facility Site Map – shared types.
 *
 * The map is stored as a GeoJSON FeatureCollection. Each feature carries a
 * `SiteMapFeatureProperties` object that describes what it represents in the
 * context of emergency-response site planning.
 *
 * Required paper submission elements (per EPCRA / first-responder standards):
 *   • Chemical storage locations (numbered, linked to inventory)
 *   • Pathways / access routes to those locations
 *   • Street intersections surrounding the facility (provided by OSM tiles)
 *   • Building outline with entrances, doorways, cylinders, tanks
 *   • Cardinal directions (N/S/E/W) – rendered on export
 *   • Map legend with symbol definitions
 *   • Evacuation routes
 */

import type {
  CasOrCategory,
  FacilityID,
  FacilityStorageLocationID,
  ProductID,
  SiteMapFeature,
  SiteMapFeatureCollection,
  SiteMapFeatureKind,
  SiteMapFeatureProperties,
  SiteMapFeatureType,
  SiteMapStrokePattern,
} from '../../tri-shared/types/db.ts';
import type { ISO8601Timestamp } from '../../cross-app-shared/types/common.ts';

export const SITE_MAP_STROKE_PATTERNS: Record<SiteMapStrokePattern, { label: string; dash: number[] }> = {
  solid: { label: 'Solid', dash: [] },
  dashed: { label: 'Dashed', dash: [3, 2] },
  dotted: { label: 'Dotted', dash: [1, 2] },
  'dash-dot': { label: 'Dash-dot', dash: [5, 2.5, 1, 2.5] },
};

/** The full site map record (mirrors the DB row). */
export interface TierIISiteMap {
  facilityID: FacilityID;
  year: number;
  /** Map centre latitude (WGS-84). */
  centerLat: number;
  /** Map centre longitude (WGS-84). */
  centerLng: number;
  /** Leaflet zoom level (typically 17–19 for a facility site). */
  zoom: number;
  features: SiteMapFeatureCollection;
  /** Firebase Storage path to an uploaded floor plan / blueprint image. */
  floorPlanStoragePath: string | null;
  /** [[south, west], [north, east]] lat/lng bounds for the floor plan overlay. */
  floorPlanBounds: [[number, number], [number, number]] | null;
  /** The row is stamped on every write, so a loaded map always has one. */
  updatedAt: ISO8601Timestamp;
}

/** Polygon fill opacity shared by drawing, saved map rendering, and PDF export. */
export const SITE_MAP_POLYGON_FILL_OPACITY = 0.15;

/**
 * The subset of feature properties the symbol renderers read. Legend swatches
 * and the drawing-tool's preview icon need a symbol without a feature behind
 * it, so they build one of these rather than a half-filled property set.
 */
export type SiteMapSymbolSpec = { color: string; storageLocationID: FacilityStorageLocationID | null } & (
  | { featureType: 'chemical-location'; chemicalNumber: number }
  | { featureType: Exclude<SiteMapFeatureType, 'chemical-location'> }
);

/** The subset of feature properties that determines a stroke/fill style. */
export interface SiteMapStyleSpec {
  featureType: SiteMapFeatureType;
  color: string;
  strokePattern: SiteMapStrokePattern | null;
}

/** Default colours by feature type (used by editor and PDF renderer). */
export const SITE_MAP_COLORS: Record<SiteMapFeatureType, string> = {
  'chemical-location': '#dc2626', // red-600
  pathway: '#2563eb', // blue-600
  'evacuation-route': '#16a34a', // green-600
  'building-outline': '#374151', // gray-700
  'property-boundary': '#a16207', // yellow-700
  room: '#6b7280', // gray-500
  entrance: '#d97706', // amber-600
  cylinder: '#7c3aed', // violet-600
  tank: '#0891b2', // cyan-600
  'street-label': '#6b7280', // gray-500
  annotation: '#1f2937', // gray-800
};

/** Human-readable labels for each feature type (used in legend). */
export const SITE_MAP_LABELS: Record<SiteMapFeatureType, string> = {
  'chemical-location': 'Chemical Storage Location',
  pathway: 'Access Pathway',
  'evacuation-route': 'Evacuation Route',
  'building-outline': 'Building Outline',
  'property-boundary': 'Property Boundary',
  room: 'Room / Area',
  entrance: 'Entrance / Doorway',
  cylinder: 'Cylinder Storage',
  tank: 'Storage Tank',
  'street-label': 'Street / Intersection Label',
  annotation: 'Annotation',
};

// ── Export / print layout configuration ───────────────────────────────────────

/** Page orientation for the exported PDF. */
export type SiteMapOrientation = 'portrait' | 'landscape';

/**
 * A physical storage vessel from the facility's capacity-based inventory,
 * used for drag-and-drop placement in the site map editor.
 */
export interface SiteMapVesselItem {
  /** Matches `CapacityLine.id`. */
  id: string;
  /** Vessel description (e.g. "55-gal drum"). */
  description: string;
  /** Product name the vessel belongs to. */
  productName: string;
  /** Site-map feature to place on drop. */
  featureType: 'cylinder' | 'tank';
}

/** Geographic rectangle (WGS-84 degrees) captured for the export. */
export interface SiteMapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/** What every legend entry carries, whichever kind it is. */
export interface SiteMapLegendItemCommon {
  /**
   * Identity used to match a saved, user-arranged legend against freshly
   * derived entries: `type:<featureType>`, `location:<id>`, `chem:<index>`
   * or `vessel:<index>`.
   */
  key: string;
  /** The (editable) display text. */
  label: string;
  /** Generated label, so refreshing features can distinguish custom legend text. */
  sourceLabel: string;
  visible: boolean;
  /** Current feature color, used for swatches and numbered chemical symbols. */
  color: string;
}

/**
 * A single, user-arrangeable legend entry for the exported PDF. The array
 * order is the render order; `kind` decides which symbol is drawn beside the
 * label and what the entry needs in order to draw it.
 */
export type SiteMapLegendItem = SiteMapLegendItemCommon &
  (
    | { kind: 'swatch'; featureType: SiteMapFeatureType; strokePattern: SiteMapStrokePattern }
    | {
        kind: 'location';
        featureType: SiteMapFeatureType;
        strokePattern: SiteMapStrokePattern;
        storageLocationID: FacilityStorageLocationID;
        /** A Point storage location draws a pin; anything else draws its category swatch. */
        geometryType: SiteMapFeature['geometry']['type'];
      }
    | { kind: 'chemical'; number: number }
    | { kind: 'vessel'; featureType: 'cylinder' | 'tank' }
  );

/**
 * A draggable/resizable layout box, expressed as a fraction (0–1) of the
 * page width/height, top-left origin (y increases downward — plain CSS
 * convention). Resolution- and orientation-independent: the same fractions
 * are re-applied against whichever PAGE_W/PAGE_H is current.
 */
export interface SiteMapElementRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Position/size/font-size of the PDF's header, map and legend blocks (Export
 * Setup "resize/move" controls). Always complete: the Export Setup dialog
 * seeds it from `defaultSiteMapLayout()` for the current orientation and the
 * user's drags mutate that seed, so the renderer never has to guess a block.
 */
export interface SiteMapLayout {
  header: SiteMapElementRect;
  map: SiteMapElementRect;
  legend: SiteMapElementRect;
  titleFontSize: number;
  legendFontSize: number;
  /** Extra vertical gap (pt) added between legend rows, on top of the font-driven default. */
  legendRowSpacing: number;
}

/** User-configurable options captured by the Export Setup dialog. */
export interface SiteMapExportConfig {
  orientation: SiteMapOrientation;
  /** Header title line (defaults to "TIER II FACILITY SITE MAP" when blank). */
  title: string;
  /** Ordered, editable legend entries. Empty ⇒ auto-generate from features. */
  legend: SiteMapLegendItem[];
  /**
   * Exact geographic rectangle to render (from the preview map). When set
   * the PDF renders this bbox into the map window for a WYSIWYG result; null
   * falls back to the saved center/zoom.
   */
  bounds: SiteMapBounds | null;
  /** Position/size/font-size of the header/map/legend blocks. */
  layout: SiteMapLayout;
}

/** Default title shown in the PDF header. */
export const SITE_MAP_DEFAULT_TITLE = 'TIER II FACILITY SITE MAP';

/**
 * Page + map + legend geometry (PDF points) for a given orientation. Single
 * source of truth shared by the PDF renderer and the Export Setup preview so
 * the on-screen map area has exactly the same aspect ratio as the printed map.
 */
export function siteMapPageGeometry(orientation: SiteMapOrientation) {
  const portrait = orientation !== 'landscape';
  const PAGE_W = portrait ? 612 : 792; // 8.5" × 11" / 11" × 8.5"
  const PAGE_H = portrait ? 792 : 612;
  const MARGIN = 28;
  const HEADER_H = 72;
  const LEGEND_H = portrait ? 140 : 96;
  const MAP_X = MARGIN + 20; // extra room for W cardinal label
  const MAP_Y = MARGIN + HEADER_H + 8;
  const MAP_W = PAGE_W - MARGIN * 2 - 20; // leave room for E label
  const MAP_H = PAGE_H - MARGIN * 2 - HEADER_H - LEGEND_H - 16;
  // Fixed-width legend column grid — shared by the PDF renderer and the
  // Export Setup preview so entries line up in disciplined rows/columns
  // instead of wrapping raggedly at variable widths.
  const LEGEND_COL_W = 170;
  const LEGEND_COLS = Math.max(3, Math.floor((PAGE_W - MARGIN * 2) / LEGEND_COL_W));
  return { PAGE_W, PAGE_H, MARGIN, HEADER_H, LEGEND_H, MAP_X, MAP_Y, MAP_W, MAP_H, LEGEND_COL_W, LEGEND_COLS };
}

/**
 * Default (fraction-based) layout boxes + font sizes for a given orientation.
 * Single source of truth for the Export Setup dialog's editable/draggable
 * layout and for any caller that needs a layout without a dialog. Derived from
 * `siteMapPageGeometry` so the un-customized defaults reproduce the original
 * fixed layout exactly.
 *
 * `y` is top-down (distance from the page's top edge), matching CSS `top` —
 * NOT pdf-lib's bottom-up page coordinate. Convert with `layoutRectToPoints`
 * and, for pdf-lib drawing calls, `PAGE_H - r.y - r.h`.
 */
export function defaultSiteMapLayout(orientation: SiteMapOrientation): SiteMapLayout {
  const g = siteMapPageGeometry(orientation);
  const toRect = (x: number, yTop: number, w: number, h: number): SiteMapElementRect => ({
    x: x / g.PAGE_W,
    y: yTop / g.PAGE_H,
    w: w / g.PAGE_W,
    h: h / g.PAGE_H,
  });
  return {
    header: toRect(g.MARGIN, g.MARGIN, g.PAGE_W - g.MARGIN * 2, g.HEADER_H),
    map: toRect(g.MAP_X, g.MAP_Y, g.MAP_W, g.MAP_H),
    legend: toRect(g.MARGIN, g.PAGE_H - g.MARGIN - g.LEGEND_H, g.PAGE_W - g.MARGIN * 2, g.LEGEND_H),
    titleFontSize: 14,
    legendFontSize: 7.5,
    legendRowSpacing: 0,
  };
}

/**
 * Print settings for a site map that was never taken through the Export Setup
 * dialog — the server-generated PDF archived with a submission package. An
 * empty legend tells the renderer to auto-generate one from the features.
 */
export function defaultSiteMapExportConfig(orientation: SiteMapOrientation): SiteMapExportConfig {
  return {
    orientation,
    title: SITE_MAP_DEFAULT_TITLE,
    legend: [],
    bounds: null,
    layout: defaultSiteMapLayout(orientation),
  };
}

/** Scale a fractional layout rect up to absolute points (still top-left origin). */
export function layoutRectToPoints(
  rect: SiteMapElementRect,
  pageW: number,
  pageH: number,
): { x: number; y: number; w: number; h: number } {
  return { x: rect.x * pageW, y: rect.y * pageH, w: rect.w * pageW, h: rect.h * pageH };
}

/**
 * Build the default legend entries from a feature collection: one swatch per
 * feature type present, followed by one numbered entry per chemical location.
 * Used by the editor to seed the Export Setup dialog and by the PDF renderer
 * as a fallback when no explicit legend is supplied.
 */
export function buildDefaultLegend(
  features: SiteMapFeature[],
  chemicalNames: (cas: CasOrCategory | `mix:${ProductID}`) => string | null,
): SiteMapLegendItem[] {
  const items: SiteMapLegendItem[] = [];

  // Generic type swatches — skip cylinder/tank (listed individually below)
  // and chemical-location (listed as numbered entries below).
  const unlinked = features.filter((f) => !f.properties.storageLocationID);
  const typesPresent = [...new Set(unlinked.map((f) => f.properties.featureType))];
  for (const ft of typesPresent) {
    if (ft === 'cylinder' || ft === 'tank' || ft === 'chemical-location') continue;
    const groups = new Map<string, SiteMapFeature[]>();
    for (const feature of unlinked.filter((f) => f.properties.featureType === ft)) {
      const color = siteMapFeatureColor(feature.properties);
      const key = `${color}:${siteMapStrokePattern(feature.properties)}`;
      groups.set(key, [...(groups.get(key) ?? []), feature]);
    }
    [...groups.values()].forEach((members, index) => {
      const color = siteMapFeatureColor(members[0]!.properties);
      const strokePattern = siteMapStrokePattern(members[0]!.properties);
      const names = members
        .map((f) => f.properties.label)
        .filter(Boolean)
        .join(', ');
      const label = groups.size > 1 ? `${SITE_MAP_LABELS[ft]} - ${names || `Group ${index + 1}`}` : SITE_MAP_LABELS[ft];
      items.push({
        kind: 'swatch',
        key: index === 0 ? `type:${ft}` : `type:${ft}:${index}`,
        label,
        sourceLabel: label,
        visible: true,
        color,
        featureType: ft,
        strokePattern,
      });
    });
  }

  // Entries for pins and areas backed by a shared facility storage location.
  for (const f of features) {
    const p = f.properties;
    if (p.storageLocationID == null) continue;
    const label = p.label ?? 'Storage location';
    items.push({
      kind: 'location',
      key: `location:${p.storageLocationID}`,
      label,
      sourceLabel: label,
      visible: true,
      color: siteMapFeatureColor(p),
      featureType: p.featureType,
      strokePattern: siteMapStrokePattern(p),
      storageLocationID: p.storageLocationID,
      geometryType: f.geometry.type,
    });
  }

  // Numbered chemical-location entries.
  let chemIndex = 0;
  for (const f of unlinked) {
    const p = f.properties;
    if (p.featureType !== 'chemical-location') continue;
    const entryKey = p.entryKey;
    const name = (entryKey ? chemicalNames(entryKey) : null) ?? p.label ?? entryKey ?? '';
    items.push({
      kind: 'chemical',
      key: `chem:${chemIndex++}`,
      label: name,
      sourceLabel: name,
      visible: true,
      number: p.chemicalNumber,
      color: siteMapFeatureColor(p),
    });
  }

  // Individual vessel entries: each placed cylinder/tank gets its own legend row
  // showing the vessel description and the product it holds.
  let vesselIndex = 0;
  for (const f of unlinked) {
    const p = f.properties;
    if (p.featureType !== 'cylinder' && p.featureType !== 'tank') continue;
    const desc = p.label ?? SITE_MAP_LABELS[p.featureType];
    const displayLabel = p.vessel ? `${desc} – ${p.vessel.productName}` : desc;
    items.push({
      kind: 'vessel',
      key: `vessel:${vesselIndex++}`,
      label: displayLabel,
      sourceLabel: displayLabel,
      visible: true,
      color: siteMapFeatureColor(p),
      featureType: p.featureType,
    });
  }

  return items;
}

/**
 * Build a feature's properties from the part that depends on its type, so a
 * caller decides that much and nothing is left implicitly unset.
 */
export function siteMapFeatureProperties(kind: SiteMapFeatureKind, color: string): SiteMapFeatureProperties {
  return { ...kind, color, label: null, strokePattern: null, labelPosition: null, storageLocationID: null };
}

/** Everything the symbol renderers need from a stored feature. */
export function siteMapSymbolSpec(properties: SiteMapFeatureProperties): SiteMapSymbolSpec {
  const { color, storageLocationID } = properties;
  return properties.featureType === 'chemical-location'
    ? { featureType: properties.featureType, chemicalNumber: properties.chemicalNumber, color, storageLocationID }
    : { featureType: properties.featureType, color, storageLocationID };
}

export function siteMapFeatureColor(properties: { featureType: SiteMapFeatureType; color: string }): string {
  return /^#[0-9a-f]{6}$/i.test(properties.color)
    ? properties.color.toLowerCase()
    : SITE_MAP_COLORS[properties.featureType];
}

export function siteMapStrokePattern(properties: SiteMapStyleSpec): SiteMapStrokePattern {
  if (properties.strokePattern && Object.hasOwn(SITE_MAP_STROKE_PATTERNS, properties.strokePattern))
    return properties.strokePattern;
  if (properties.featureType === 'evacuation-route') return 'dash-dot';
  if (properties.featureType === 'pathway' || properties.featureType === 'property-boundary') return 'dashed';
  if (properties.featureType === 'annotation') return 'dotted';
  return 'solid';
}

/** Refresh feature-derived styling while retaining the user's order, visibility and edited labels. */
export function refreshSiteMapLegend(saved: readonly SiteMapLegendItem[], current: readonly SiteMapLegendItem[]) {
  const remaining = new Map(current.map((item) => [item.key, item]));
  const result: SiteMapLegendItem[] = [];
  for (const old of saved) {
    const fresh = remaining.get(old.key);
    if (!fresh) continue;
    result.push({
      ...fresh,
      visible: old.visible,
      label: fresh.kind === 'location' ? fresh.label : old.label !== old.sourceLabel ? old.label : fresh.label,
    });
    remaining.delete(old.key);
  }
  return [...result, ...remaining.values()];
}
